/**
 * Slice des Méta Notes (C28 SP1, spec § 8) : la liste de mes plateaux et le plateau
 * actif, modifié localement geste par geste (`modifierPlateau`) et enregistré entier
 * avec sa version (`enregistrerMetaNote`). L'état d'enregistrement se lit dans
 * l'en-tête du plateau : « Enregistré », « Non enregistré », conflit (409, un autre
 * onglet a écrit : on n'écrase rien, on recharge), échec.
 */
import { createSlice, createAsyncThunk, type PayloadAction } from '@reduxjs/toolkit';
import { metanoteService } from '../../services/metanoteAPI';
import { getTokenRedirect } from '../../utils/authRedirect';
import { tokenRequest } from '../../authConfig';
import type { Carte, Fleche, Ilot, MetaNote, MetaNoteResume } from '../../types/metanote';
import { flechesTenues } from '../../utils/metanote/fleches';

/** Le plateau tel qu'il entre dans le store : ses flèches pendantes écartées (C28 SP4, choix C11), `fleches` toujours présent. */
const assainir = (m: MetaNote): MetaNote => ({ ...m, fleches: flechesTenues(m.fleches, m.cartes, m.ilots) });

export type Enregistrement = 'enregistre' | 'en_attente' | 'en_cours' | 'conflit' | 'echec';
export type MetaNotesStatut = 'idle' | 'loading' | 'ready' | 'error';

export interface MetaNotesState {
    liste: MetaNoteResume[];
    statut: MetaNotesStatut;
    actif: MetaNote | null;
    actifStatut: MetaNotesStatut;
    enregistrement: Enregistrement;
    /** Une modification est arrivée pendant un envoi : il faudra renvoyer. */
    modifieDepuisEnvoi: boolean;
    /** Le plateau actif est à l'écran (`Plateau` monté) : son enregistrement automatique tourne (C28 SP3). */
    affiche: boolean;
    erreur: string | null;
}

export const initialMetaNotesState: MetaNotesState = {
    liste: [], statut: 'idle', actif: null, actifStatut: 'idle', enregistrement: 'enregistre', modifieDepuisEnvoi: false, affiche: false, erreur: null,
};

async function tokenOf(instance: any): Promise<string> {
    const accessToken = await getTokenRedirect(tokenRequest, instance);
    if (!accessToken) throw new Error('Impossible d\'obtenir le token d\'accès');
    return accessToken;
}
const messageOf = (error: any, fallback: string): string => error?.response?.data?.message || error?.message || fallback;

export const listerMetaNotes = createAsyncThunk<MetaNoteResume[], { instance: any }, { rejectValue: string }>(
    'metanotes/lister',
    async ({ instance }, { rejectWithValue }) => {
        try { return await metanoteService.lister(await tokenOf(instance)); } catch (e) { return rejectWithValue(messageOf(e, 'Impossible de lire vos plateaux')); }
    },
);

export const ouvrirMetaNote = createAsyncThunk<MetaNote, { instance: any; id: string }, { rejectValue: string }>(
    'metanotes/ouvrir',
    async ({ instance, id }, { rejectWithValue }) => {
        try { return await metanoteService.lire(id, await tokenOf(instance)); } catch (e) { return rejectWithValue(messageOf(e, 'Plateau introuvable')); }
    },
);

export const creerMetaNote = createAsyncThunk<MetaNote, { instance: any; titre: string }, { rejectValue: string }>(
    'metanotes/creer',
    async ({ instance, titre }, { rejectWithValue }) => {
        try { return await metanoteService.creer(titre, await tokenOf(instance)); } catch (e) { return rejectWithValue(messageOf(e, 'Création impossible. Réessayez.')); }
    },
);

export const enregistrerMetaNote = createAsyncThunk<MetaNote, { instance: any }, { state: { metanotes: MetaNotesState }; rejectValue: { message: string; conflit: boolean } }>(
    'metanotes/enregistrer',
    async ({ instance }, { getState, rejectWithValue }) => {
        const actif = getState().metanotes.actif;
        if (!actif) return rejectWithValue({ message: 'Aucun plateau ouvert', conflit: false });
        try {
            const { titre, cartes, ilots, version } = actif;
            // C28 SP4 : un plateau ouvert avant les flèches n'a pas le champ ; il part vide.
            return await metanoteService.enregistrer(actif._id, { titre, cartes, ilots, fleches: actif.fleches ?? [], version }, await tokenOf(instance));
        } catch (e: any) {
            return rejectWithValue({ message: messageOf(e, 'Enregistrement impossible. Réessayez.'), conflit: e?.response?.status === 409 });
        }
    },
    { condition: (_, { getState }) => { const s = getState().metanotes; return s.actif !== null && s.enregistrement !== 'en_cours' && s.enregistrement !== 'conflit'; } },
);

type RejetVersionne = { message: string; conflit: boolean };
const rejetVersionne = (e: any, fallback: string): RejetVersionne => ({ message: messageOf(e, fallback), conflit: e?.response?.status === 409 });

/**
 * L'écriture de la note (C28 SP2, spec § 5) : le plateau se souvient de la note produite et des cartes écrites.
 * Versionnée comme l'enregistrement ; refusée tant que l'en-tête ne dit pas « Enregistré » (décision (f) du plan).
 */
export const enregistrerEcriture = createAsyncThunk<
    MetaNote,
    { instance: any; noteId: string; cartesIds: string[]; ilots: Array<{ id: string; titreEcrit: string }> },
    { state: { metanotes: MetaNotesState }; rejectValue: RejetVersionne }
>(
    'metanotes/enregistrerEcriture',
    async ({ instance, noteId, cartesIds, ilots }, { dispatch, getState, rejectWithValue }) => {
        const actif = getState().metanotes.actif;
        if (!actif) return rejectWithValue({ message: 'Aucun plateau ouvert', conflit: false });
        const echec = 'La note est écrite, mais le plateau n\'a pas pu s\'en souvenir. Réessayez.';
        try {
            const token = await tokenOf(instance);
            try {
                return await metanoteService.enregistrerEcriture(actif._id, { noteId, cartesIds, ilots, version: actif.version }, token);
            } catch (e: any) {
                if (e?.response?.status !== 409) throw e;
                // Un autre onglet a écrit le plateau. Rien n'attend ici (condition « Enregistré ») : on adopte la
                // version du serveur et on retente une fois — la fusion serveur rend l'écriture idempotente.
                // Sans cela, la note écrite serait oubliée du plateau et « Écrire » en produirait une seconde.
                const frais = await metanoteService.lire(actif._id, token);
                dispatch(metanotesSlice.actions.plateauRelu(frais));
                return await metanoteService.enregistrerEcriture(actif._id, { noteId, cartesIds, ilots, version: frais.version }, token);
            }
        } catch (e: any) {
            return rejectWithValue(rejetVersionne(e, echec));
        }
    },
    { condition: (_, { getState }) => { const s = getState().metanotes; return s.actif !== null && s.enregistrement === 'enregistre'; } },
);

/** La note écrite n'existe plus : le plateau l'oublie et « Écrire la note » redevient possible. */
export const oublierEcriture = createAsyncThunk<MetaNote, { instance: any }, { state: { metanotes: MetaNotesState }; rejectValue: RejetVersionne }>(
    'metanotes/oublierEcriture',
    async ({ instance }, { getState, rejectWithValue }) => {
        const actif = getState().metanotes.actif;
        if (!actif) return rejectWithValue({ message: 'Aucun plateau ouvert', conflit: false });
        try {
            return await metanoteService.oublierEcriture(actif._id, actif.version, await tokenOf(instance));
        } catch (e: any) {
            return rejectWithValue(rejetVersionne(e, 'Impossible d\'oublier la note. Réessayez.'));
        }
    },
    { condition: (_, { getState }) => { const s = getState().metanotes; return s.actif !== null && s.enregistrement === 'enregistre'; } },
);

export const supprimerMetaNote = createAsyncThunk<string, { instance: any; id: string }, { rejectValue: string }>(
    'metanotes/supprimer',
    async ({ instance, id }, { rejectWithValue }) => {
        try { await metanoteService.supprimer(id, await tokenOf(instance)); return id; } catch (e) { return rejectWithValue(messageOf(e, 'Suppression impossible. Réessayez.')); }
    },
);

const resumeDe = (m: MetaNote): MetaNoteResume => ({
    _id: m._id, titre: m.titre, nbCartes: m.cartes.length, nbIlots: m.ilots.length,
    ...(m.noteId ? { noteId: m.noteId } : {}), ...(m.derniereEcriture ? { derniereEcriture: m.derniereEcriture.date } : {}), updatedAt: m.updatedAt,
});

/** La réponse de la route d'écriture : noteId et derniereEcriture tels que le serveur les tient, version et liste à jour. */
function poserEcriture(s: MetaNotesState, m: MetaNote): void {
    if (!s.actif || s.actif._id !== m._id) return;
    s.actif.version = m.version;
    s.actif.updatedAt = m.updatedAt;
    if (m.noteId) s.actif.noteId = m.noteId; else delete s.actif.noteId;
    if (m.derniereEcriture) s.actif.derniereEcriture = m.derniereEcriture; else delete s.actif.derniereEcriture;
    const actif = s.actif;
    s.liste = s.liste.map(r => (r._id === actif._id ? resumeDe(actif) : r));
}

function rejeterEcriture(s: MetaNotesState, rejet: RejetVersionne | undefined): void {
    if (rejet?.conflit) s.enregistrement = 'conflit';
    s.erreur = rejet?.message ?? 'Enregistrement impossible. Réessayez.';
}

const metanotesSlice = createSlice({
    name: 'metanotes',
    initialState: initialMetaNotesState,
    reducers: {
        modifierPlateau(state, action: PayloadAction<{ titre?: string; cartes?: Carte[]; ilots?: Ilot[]; fleches?: Fleche[] }>) {
            if (!state.actif) return;
            if (action.payload.titre !== undefined) state.actif.titre = action.payload.titre;
            if (action.payload.cartes !== undefined) state.actif.cartes = action.payload.cartes;
            if (action.payload.ilots !== undefined) state.actif.ilots = action.payload.ilots;
            if (action.payload.fleches !== undefined) state.actif.fleches = action.payload.fleches;
            if (state.enregistrement === 'conflit') return;
            if (state.enregistrement === 'en_cours') state.modifieDepuisEnvoi = true;
            else state.enregistrement = 'en_attente';
        },
        /** Le plateau relu au serveur remplace l'actif (reprise d'un 409 sans rien en attente). */
        plateauRelu(state, action: PayloadAction<MetaNote>) {
            if (!state.actif || state.actif._id !== action.payload._id) return;
            state.actif = assainir(action.payload);
            state.enregistrement = 'enregistre';
            state.modifieDepuisEnvoi = false;
        },
        /** `Plateau` se monte ou se démonte : seul un plateau affiché reçoit une pose du chat (C28 SP3). */
        plateauAffiche(state, action: PayloadAction<boolean>) {
            state.affiche = action.payload;
        },
        fermerPlateau(state) {
            state.actif = null; state.actifStatut = 'idle'; state.enregistrement = 'enregistre'; state.modifieDepuisEnvoi = false; state.erreur = null;
        },
    },
    extraReducers: (builder) => {
        builder
            .addCase(listerMetaNotes.pending, (s) => { s.statut = 'loading'; s.erreur = null; })
            .addCase(listerMetaNotes.fulfilled, (s, a) => { s.statut = 'ready'; s.liste = a.payload; })
            .addCase(listerMetaNotes.rejected, (s, a) => { s.statut = 'error'; s.erreur = a.payload ?? 'Impossible de lire vos plateaux'; })
            .addCase(ouvrirMetaNote.pending, (s) => { s.actifStatut = 'loading'; s.erreur = null; s.enregistrement = 'enregistre'; s.modifieDepuisEnvoi = false; })
            .addCase(ouvrirMetaNote.fulfilled, (s, a) => { s.actifStatut = 'ready'; s.actif = assainir(a.payload); })
            .addCase(ouvrirMetaNote.rejected, (s, a) => { s.actifStatut = 'error'; s.actif = null; s.erreur = a.payload ?? 'Plateau introuvable'; })
            .addCase(creerMetaNote.fulfilled, (s, a) => { s.actif = a.payload; s.actifStatut = 'ready'; s.enregistrement = 'enregistre'; s.liste = [resumeDe(a.payload), ...s.liste]; })
            .addCase(creerMetaNote.rejected, (s, a) => { s.erreur = a.payload ?? 'Création impossible. Réessayez.'; })
            .addCase(enregistrerMetaNote.pending, (s) => { s.enregistrement = 'en_cours'; s.modifieDepuisEnvoi = false; s.erreur = null; })
            .addCase(enregistrerMetaNote.fulfilled, (s, a) => {
                if (s.actif && s.actif._id === a.payload._id) { s.actif.version = a.payload.version; s.actif.updatedAt = a.payload.updatedAt; }
                s.enregistrement = s.modifieDepuisEnvoi ? 'en_attente' : 'enregistre';
                s.modifieDepuisEnvoi = false;
                if (s.actif) { const actif = s.actif; s.liste = s.liste.map(r => (r._id === actif._id ? resumeDe(actif) : r)); }
            })
            .addCase(enregistrerMetaNote.rejected, (s, a) => {
                s.enregistrement = a.payload?.conflit ? 'conflit' : 'echec';
                s.erreur = a.payload?.message ?? 'Enregistrement impossible. Réessayez.';
                s.modifieDepuisEnvoi = false;
            })
            .addCase(enregistrerEcriture.fulfilled, (s, a) => { poserEcriture(s, a.payload); })
            .addCase(enregistrerEcriture.rejected, (s, a) => { rejeterEcriture(s, a.payload); })
            .addCase(oublierEcriture.fulfilled, (s, a) => { poserEcriture(s, a.payload); })
            .addCase(oublierEcriture.rejected, (s, a) => { rejeterEcriture(s, a.payload); })
            .addCase(supprimerMetaNote.fulfilled, (s, a) => {
                s.liste = s.liste.filter(r => r._id !== a.payload);
                if (s.actif?._id === a.payload) { s.actif = null; s.actifStatut = 'idle'; s.enregistrement = 'enregistre'; }
            })
            .addCase(supprimerMetaNote.rejected, (s, a) => { s.erreur = a.payload ?? 'Suppression impossible. Réessayez.'; });
    },
});

export const { modifierPlateau, fermerPlateau, plateauAffiche, plateauRelu } = metanotesSlice.actions;
export default metanotesSlice.reducer;

type Etat = { metanotes: MetaNotesState };
export const selectMetaNotesListe = (state: Etat) => state.metanotes.liste;
export const selectMetaNotesStatut = (state: Etat) => state.metanotes.statut;
export const selectPlateauActif = (state: Etat) => state.metanotes.actif;
export const selectPlateauStatut = (state: Etat) => state.metanotes.actifStatut;
export const selectEnregistrement = (state: Etat) => state.metanotes.enregistrement;
export const selectMetaNotesErreur = (state: Etat) => state.metanotes.erreur;
/** La note écrite depuis le plateau actif (C28 SP2), `null` sans écriture. */
export const selectNoteDuPlateau = (state: Etat) => state.metanotes.actif?.noteId ?? null;
